<div class="block md:hidden">
    <ca-toggle-group class="block mb-6 ca-table-toggle-container" (select)="setSelectedItem($any($event))" ariaLabel="table Options">
        @for (item of items(); track item.id) {
            <ca-toggle-item
                [value]="item"
                [attr.aria-controls]="item?.id"
                [attr.aria-selected]="item === selectedItem"
                [id]="item?.id + '-button'"
                >{{ item[definitions()[0].field] }}
            </ca-toggle-item>
        }
    </ca-toggle-group>

    <div role="tabpanel" [id]="selectedItem?.id" [attr.aria-labelledby]="selectedItem?.id + '-button'">
        <ng-container *ngTemplateOutlet="singleColumnTable; context: { item: selectedItem }"></ng-container>
    </div>
</div>

<div class="hidden md:block lg:hidden">
    <div class="flex items-stretch w-full">
        <ng-container *ngTemplateOutlet="separateColumnTable"></ng-container>
    </div>
</div>

<div class="hidden lg:block">
    <div role="table" class="multiple-column-table" [class.with-padding]="withPadding()">
        @for (definition of definitions(); track definition.field; let i = $index, lastDefinition = $last) {
            @if (i === 0) {
                <div role="row" class="flex justify-stretch" [style.background-color]="headerColor()">
                    <div role="columnheader" class="p-4 header-cell">{{ definition.header }}</div>
                    @for (item of items(); track item.id) {
                        <div
                            role="columnheader"
                            class="p-4 flex header-cell"
                            [ngClass]="{ 'justify-end': !centerItems(), 'justify-center': centerItems() }"
                            [class.dark]="item?.darkStyle"
                        >
                            {{ item[definition.field] }}
                        </div>
                    }
                    @if (hasEditableDefinition) {
                        <div role="columnheader" class="p-4 flex header-cell max-w-[100px]"></div>
                    }
                    @if (hasChildren) {
                        <div role="columnheader" class="p-4 flex header-cell max-w-[50px]"></div>
                    }
                </div>
            } @else {
                <div
                    role="row"
                    class="flex row"
                    [class.expanded]="expandedItems[definition.field]"
                    [class.last]="lastDefinition"
                    [class.bottom-border-highlighted]="definition.bottomBorderHighlighted"
                    [style.background-color]="definition.isFooter ? headerColor() : ''"
                    [style.font-weight]="definition.isFooter ? 'bold' : 'normal'"
                >
                    <div role="cell" class="p-4 cell flex items-center">
                        @if (definition.headerTemplate) {
                            <ng-container
                                *ngTemplateOutlet="definition.headerTemplate.reference; context: { definition: definition }"
                            ></ng-container>
                        } @else {
                            {{ definition.header }}
                        }
                    </div>
                    @for (item of items(); track item.id) {
                        <div
                            role="cell"
                            class="p-4 flex flex-col cell"
                            [ngClass]="{ 'items-end': !centerItems(), 'items-center': centerItems() }"
                            [class.dark]="item?.darkStyle"
                        >
                            @if (isEdited(definition)) {
                                <mat-form-field
                                    appearance="outline"
                                    subscriptSizing="dynamic"
                                    [hideRequiredMarker]="true"
                                    class="ca-form-field ca-form-field-xs"
                                >
                                    <input type="number" class="text-right" matInput [formControl]="getFormControl(item.id)" />
                                    @if (definition.negative) {
                                        <span matPrefix>-</span>
                                    }
                                    @if (item[definition.field]?.unit) {
                                        <span matSuffix>{{ item[definition.field].unit }}</span>
                                    }
                                    @if (item[definition.field]?.hint) {
                                        <mat-hint>{{ item[definition.field].hint }}</mat-hint>
                                    }
                                </mat-form-field>
                            } @else {
                                @if (definition.template) {
                                    <ng-container
                                        *ngTemplateOutlet="definition.template.reference; context: { value: item?.[definition.field] }"
                                    ></ng-container>
                                } @else {
                                    <span data-testid="desktop-value">{{ item[definition.field].value }}</span>
                                }
                            }
                        </div>
                    }
                    @if (hasEditableDefinition) {
                        <div role="cell" class="p-4 flex flex-col items-end justify-center cell max-w-[100px]">
                            @if (definition.editable && !isEdited(definition)) {
                                <ca-icon icon="pencil" class="cursor-pointer ca-icon-s-16-p-0-f-16" (click)="onEdit(definition)"></ca-icon>
                            } @else if (isEdited(definition)) {
                                <div class="flex gap-2 items-end">
                                    <ca-icon icon="check" class="cursor-pointer ca-icon-s-32-p-0-f-32" (click)="onSave()"></ca-icon>
                                    <ca-icon icon="cross" class="cursor-pointer ca-icon-cancel" (click)="onCancel()"></ca-icon>
                                </div>
                            }
                        </div>
                    }
                    @if (hasChildren) {
                        <div role="cell" class="p-4 flex flex-col items-end justify-center cell max-w-[50px]">
                            @if (definition.children) {
                                <ca-icon
                                    icon="chevron-right"
                                    [class.-rotate-90]="expandedItems[definition.field]"
                                    [class.rotate-90]="!expandedItems[definition.field]"
                                    class="cursor-pointer ca-icon-s-16-p-0-f-16 transition-transform duration-200"
                                    (click)="toggleExpand(definition)"
                                >
                                </ca-icon>
                            }
                        </div>
                    }
                </div>

                @if (definition.children) {
                    <div
                        class="flex row"
                        [@expandCollapse]="expandedItems[definition.field] ? 'expanded' : 'collapsed'"
                        [class.hide-border]="expandedItems[definition.field]"
                    >
                        <div role="table" class="nested-table">
                            @for (childDefinition of definition.children; track childDefinition.field) {
                                <div
                                    role="row"
                                    class="flex row"
                                    [class.bottom-border-highlighted]="childDefinition.bottomBorderHighlighted"
                                >
                                    <div role="cell" class="p-4 pl-8 cell">
                                        @if (childDefinition.headerTemplate) {
                                            <ng-container
                                                *ngTemplateOutlet="
                                                    childDefinition.headerTemplate.reference;
                                                    context: { definition: childDefinition }
                                                "
                                            ></ng-container>
                                        } @else {
                                            {{ childDefinition.header }}
                                        }
                                    </div>
                                    @for (item of items(); track item.id) {
                                        <div
                                            role="cell"
                                            class="p-4 flex flex-col cell"
                                            [ngClass]="{ 'items-end': !centerItems(), 'items-center': centerItems() }"
                                        >
                                            @if (isEdited(childDefinition)) {
                                                <mat-form-field
                                                    appearance="outline"
                                                    subscriptSizing="dynamic"
                                                    [hideRequiredMarker]="true"
                                                    class="ca-form-field ca-form-field-xs"
                                                >
                                                    <input
                                                        type="number"
                                                        class="text-right"
                                                        matInput
                                                        [formControl]="getFormControl(item.id)"
                                                    />
                                                    @if (childDefinition.negative) {
                                                        <span matPrefix>-</span>
                                                    }
                                                    @if (item[definition.field]?.children?.[childDefinition.field]?.unit) {
                                                        <span matSuffix>{{
                                                            item[definition.field]?.children?.[childDefinition.field]?.unit
                                                        }}</span>
                                                    }
                                                    @if (item[definition.field]?.children?.[childDefinition.field]?.hint) {
                                                        <mat-hint>{{
                                                            item[definition.field]?.children?.[childDefinition.field]?.hint
                                                        }}</mat-hint>
                                                    }
                                                </mat-form-field>
                                            } @else {
                                                @if (childDefinition.template) {
                                                    <ng-container
                                                        *ngTemplateOutlet="
                                                            childDefinition.template.reference;
                                                            context: { value: item[definition.field]?.children?.[childDefinition.field] }
                                                        "
                                                    >
                                                    </ng-container>
                                                } @else {
                                                    <span data-testid="nested-desktop-value">{{
                                                        item[definition.field]?.children?.[childDefinition.field]?.value
                                                    }}</span>
                                                }
                                            }
                                        </div>
                                    }
                                    @if (hasEditableDefinition) {
                                        <div role="cell" class="p-4 flex flex-col items-end justify-center cell max-w-[100px]">
                                            @if (childDefinition.editable && !isEdited(childDefinition)) {
                                                <ca-icon
                                                    icon="pencil"
                                                    class="cursor-pointer ca-icon-s-16-p-0-f-16"
                                                    (click)="onEdit(definition, childDefinition)"
                                                ></ca-icon>
                                            } @else if (isEdited(childDefinition)) {
                                                <div class="flex gap-2 items-end">
                                                    <ca-icon
                                                        icon="check"
                                                        class="cursor-pointer ca-icon-s-32-p-0-f-32"
                                                        (click)="onSave()"
                                                    ></ca-icon>
                                                    <ca-icon
                                                        icon="cross"
                                                        class="cursor-pointer ca-icon-cancel"
                                                        (click)="onCancel()"
                                                    ></ca-icon>
                                                </div>
                                            }
                                        </div>
                                    }
                                    <div role="cell" class="p-4 flex flex-col items-end justify-start cell max-w-[50px]"></div>
                                </div>
                            }
                        </div>
                    </div>
                }
            }
        }
    </div>
</div>

<ng-template #separateColumnTable>
    <div role="table" class="separate-column-table">
        @for (definition of definitions(); track definition.field; let i = $index) {
            @if (i === 0) {
                <div role="row" class="flex justify-stretch header-row">
                    @for (item of items(); track item.id; let isFirst = $first; let isLast = $last) {
                        <div
                            role="columnheader"
                            class="p-4 flex justify-center header-cell"
                            [ngClass]="{ 'me-2': !isLast, 'ms-2': !isFirst, dark: item?.darkStyle }"
                        >
                            {{ item[definition.field] }}
                        </div>
                    }
                </div>
            } @else {
                <div role="row" class="flex row">
                    @for (item of items(); track item.id; let isFirst = $first; let isLast = $last) {
                        <div
                            role="cell"
                            class="p-4 flex flex-col items-center cell"
                            [ngClass]="{ 'me-2': !isLast, 'ms-2': !isFirst, dark: item?.darkStyle }"
                        >
                            <span class="mb-4">
                                @if (definition.headerTemplate) {
                                    <ng-container
                                        *ngTemplateOutlet="definition.headerTemplate.reference; context: { definition: definition }"
                                    ></ng-container>
                                } @else {
                                    <span class="ca-text-md"> {{ definition.header }} </span>
                                }
                            </span>

                            @if (definition.template) {
                                <ng-container
                                    *ngTemplateOutlet="definition.template.reference; context: { value: item?.[definition.field] }"
                                ></ng-container>
                            } @else {
                                <span data-testid="tablet-value">{{ item[definition.field].value }}</span>
                            }
                        </div>
                    }
                </div>
            }
        }
    </div>
</ng-template>

<ng-template #singleColumnTable let-item="item">
    <div role="table" class="single-column-table" [class.dark]="item?.darkStyle">
        @for (definition of definitions(); track definition.field; let definitionIndex = $index) {
            @if (definitionIndex === 0) {
                <div role="row" class="header-row">
                    <div role="columnheader" class="flex justify-center p-4 header-cell">
                        {{ item?.[definition.field] }}
                    </div>
                </div>
            } @else {
                <div role="row" class="row">
                    <div role="cell" class="flex flex-col items-center p-4 cell">
                        <span class="mb-4">
                            @if (definition.headerTemplate) {
                                <ng-container
                                    *ngTemplateOutlet="definition.headerTemplate.reference; context: { definition: definition }"
                                ></ng-container>
                            } @else {
                                <span class="ca-text-md"> {{ definition.header }} </span>
                            }
                        </span>
                        @if (definition.template) {
                            <ng-container
                                *ngTemplateOutlet="definition.template.reference; context: { value: item?.[definition.field] }"
                            ></ng-container>
                        } @else {
                            <span data-testid="mobile-value">{{ item[definition.field].value }}</span>
                        }
                    </div>
                </div>
            }
        }
    </div>
</ng-template>
